<template>
  <component :is="wrapperTag" v-bind="tooltipAttrs">
    <span class="row-status-icon" :class="`--status-${status}`">
      <svg
        v-if="status === 'new'"
        data-variant="new"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7zm-1-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
        />
      </svg>
      <svg
        v-else-if="status === 'existing'"
        data-variant="existing"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M12 6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8c-.45-.83-.7-1.79-.7-2.8 0-3.31 2.69-6 6-6zm6.76 1.74L17.3 9.2c.44.84.7 1.79.7 2.8 0 3.31-2.69 6-6 6v-3l-4 4 4 4v-3c4.42 0 8-3.58 8-8 0-1.57-.46-3.03-1.24-4.26z"
        />
      </svg>
      <svg
        v-else-if="status === 'removed'"
        data-variant="removed"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M7 11v2h10v-2H7zm5-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
        />
      </svg>
      <svg
        v-else-if="status === 'skipped'"
        data-variant="skipped"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z"
        />
      </svg>
      <svg
        v-else-if="status === 'unchanged'"
        data-variant="unchanged"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm4.59-12.42L10 14.17l-2.59-2.58L6 13l4 4 8-8z"
        />
      </svg>
      <svg
        v-else-if="status === 'warning'"
        data-variant="warning"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
      </svg>
      <svg v-else data-variant="error" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
        <path
          d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
        />
      </svg>
    </span>
  </component>
</template>

<script setup>
/**
 * Standard status icon for SpAdmin bulk-preview table rows: circled plus for
 * new records, circular refresh arrows for existing records that will be
 * updated, a circled minus for records that will be removed, a block sign for
 * rows that are skipped, a circled check for rows left unchanged, a triangle
 * for nonfatal warnings, and a cross for rows that failed validation. Wraps
 * itself in an sp-tooltip when a label is provided.
 */
import { computed } from "vue";

const props = defineProps({
  /** Row state driving the glyph and colour. */
  status: {
    type: String,
    required: true,
    validator: (value) => ["new", "existing", "removed", "skipped", "unchanged", "warning", "error"].includes(value),
  },
  /** Optional tooltip text. When absent the icon renders without a tooltip wrapper. */
  label: {
    type: String,
    default: "",
  },
});

const wrapperTag = computed(() => (props.label ? "sp-tooltip" : "span"));

const tooltipAttrs = computed(() => {
  if (!props.label) {
    return {};
  }

  return { position: "right", theme: "dark", content: props.label };
});
</script>

<style scoped lang="scss">
.row-status-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }

  &.--status-new {
    color: var(--sp-sys-color-success, #2e7d32);
  }

  &.--status-existing {
    color: var(--sp-sys-color-info, #0288d1);
  }

  &.--status-removed {
    color: var(--sp-sys-color-error);
  }

  &.--status-skipped {
    color: var(--sp-sys-color-on-surface-variant);
  }

  &.--status-unchanged {
    color: var(--sp-sys-color-on-surface-variant);
  }

  &.--status-warning {
    // The system warning token is a pale yellow that disappears on the light
    // surface, so the default is a darker amber; the undeclared component token
    // leaves the colour themable (same pattern as the changeset-diff colours).
    color: var(--sp-comp-row-status-icon-warning-color, #b26a00);
  }

  &.--status-error {
    color: var(--sp-sys-color-error, #d32f2f);
  }
}
</style>
